// src/pages/UserProjectDetails.js

import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

import { auth, db } from "../services/firebase";

import ComparisonTable from "../components/ComparisonTable";
import KitchenSchematic from "./KitchenSchematic";
import Kitchen3DView from "../components/kitchen/Kitchen3DView";

/* =========================================================
   HELPERS
   ========================================================= */

function formatMoney(v) {
  const n = Number(v);

  if (!Number.isFinite(n)) {
    return "—";
  }

  return `$${n.toFixed(2)}`;
}

function formatDate(ts) {
  try {
    if (ts?.toDate) {
      return ts.toDate().toLocaleString();
    }

    return new Date(ts).toLocaleString();
  } catch {
    return "—";
  }
}

function feetInches(feet) {
  if (
    feet == null ||
    !Number.isFinite(Number(feet))
  ) {
    return "—";
  }

  const totalIn =
    Math.round(Number(feet) * 12);

  const ft =
    Math.floor(totalIn / 12);

  const inch =
    totalIn % 12;

  if (
    ft === 0 &&
    inch === 0
  ) {
    return "0\u2033";
  }

  if (inch === 0) {
    return `${ft}\u2032`;
  }

  if (ft === 0) {
    return `${inch}\u2033`;
  }

  return `${ft}\u2032 ${inch}\u2033`;
}

/* =========================================================
   COMPONENT
   ========================================================= */

export default function UserProjectDetails() {
  const navigate =
    useNavigate();

  const { projectId } =
    useParams();

  const [
    authReady,
    setAuthReady,
  ] = useState(false);

  const [
    project,
    setProject,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  /* =======================================================
     AUTH WARMUP
     ======================================================= */

  useEffect(() => {
    const unsub =
      onAuthStateChanged(
        auth,
        async (u) => {
          try {
            if (u) {
              await u.getIdToken(
                true
              );
            }
          } catch (e) {
            console.warn(
              "Token warmup failed:",
              e
            );
          }

          setAuthReady(true);
        }
      );

    return () =>
      unsub();
  }, []);

  /* =======================================================
     LOAD PROJECT
     ======================================================= */

  const load = async () => {
    setLoading(true);
    setError("");

    try {
      const u =
        auth.currentUser;

      if (!u) {
        setError(
          "Not signed in."
        );

        setProject(null);

        return;
      }

      await u.getIdToken(
        true
      );

      const snap =
        await getDoc(
          doc(
            db,
            "projects",
            projectId
          )
        );

      if (!snap.exists()) {
        setError(
          "Project not found."
        );

        setProject(null);

        return;
      }

      setProject({
        id: snap.id,
        ...snap.data(),
      });
    } catch (e) {
      console.error(
        "UserProjectDetails load error:",
        e
      );

      setError(
        e?.message ||
          "Failed to load project."
      );

      setProject(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authReady) {
      return;
    }

    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authReady,
    projectId,
  ]);

  /* =======================================================
     LOADING
     ======================================================= */

  if (loading) {
    return (
      <div
        style={{
          maxWidth: 980,
          margin: "0 auto",
          padding: 20,
        }}
      >
        <h2
          style={{
            color: "#0b3a6a",
            fontWeight: 900,
          }}
        >
          Project Details
        </h2>

        <div
          style={{
            fontWeight: 900,
          }}
        >
          Loading project…
        </div>
      </div>
    );
  }

  /* =======================================================
     ERROR
     ======================================================= */

  if (
    !project ||
    error
  ) {
    return (
      <div
        style={{
          maxWidth: 980,
          margin: "0 auto",
          padding: 20,
        }}
      >
        <h2
          style={{
            color: "#0b3a6a",
            fontWeight: 900,
          }}
        >
          Project Details
        </h2>

        <p>
          {error ||
            "Project not found."}
        </p>

        <div
          style={{
            display: "flex",
            gap: 10,
          }}
        >
          <button
            onClick={() =>
              navigate(
                "/dashboard"
              )
            }
            style={
              btnPrimary
            }
          >
            Back to Dashboard
          </button>

          <button
            onClick={
              load
            }
            style={
              btnDark
            }
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  /* =======================================================
     TYPE DETECTION
     ======================================================= */

  const isSurface =
    project.projectType ===
    "surface";

  const isKitchen =
    project.projectType ===
    "kitchen";

  const rooms =
    Array.isArray(
      project.rooms
    )
      ? project.rooms
      : [];

  const summary =
    project.summary || {};

  /* =======================================================
     KITCHEN WALLS
     ======================================================= */

  /*
   * Kitchen walls are saved as an object keyed by role.
   *
   * Example:
   *
   * {
   *   left: {...},
   *   back: {...},
   *   right: {...}
   * }
   *
   * Keep a predictable display order for the
   * Source Measurements section.
   */

  const KITCHEN_ORDER = [
    "left",
    "back",
    "side",
    "right",
  ];

  const kitchenWalls =
    isKitchen &&
    project.walls
      ? Object.values(
          project.walls
        ).sort(
          (a, b) =>
            KITCHEN_ORDER.indexOf(
              a.role
            ) -
            KITCHEN_ORDER.indexOf(
              b.role
            )
        )
      : [];

  /*
   * This is the key to reconstructing the saved 3D survey.
   *
   * We do NOT store a 3D screenshot.
   *
   * Instead:
   *
   * project.layout + project.walls
   *
   * are passed back into the exact same Kitchen3DView
   * component used during Kitchen Mode.
   */

  const canRenderKitchen3D =
    isKitchen &&
    Boolean(
      project.layout
    ) &&
    Boolean(
      project.walls
    ) &&
    kitchenWalls.length >
      0;

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <div
      style={{
        maxWidth: 980,
        margin: "0 auto",
        padding: 20,
      }}
    >
      {/* ===================================================
          HEADER
          =================================================== */}

      <div
        style={
          headerRow
        }
      >
        <div>
          <h2
            style={
              title
            }
          >
            {project.projectName ||
              `Project ${project.id.slice(
                -8
              )}`}
          </h2>

          <div
            style={
              subtle
            }
          >
            Created:{" "}
            {formatDate(
              project.createdAt
            )}
          </div>
        </div>

        <Link
          to="/dashboard"
          style={
            btnBack
          }
        >
          Back
        </Link>
      </div>

      {/* ===================================================
          SURFACE MODE
          =================================================== */}

      {isSurface && (
        <>
          <div
            style={
              card
            }
          >
            <h3
              style={
                sectionTitle
              }
            >
              Surface Measurement
            </h3>

            {project.surfaceUrl ? (
              <img
                src={
                  project.surfaceUrl
                }
                alt="Surface"
                style={
                  imgStyle
                }
              />
            ) : (
              <div
                style={
                  emptyNote
                }
              >
                No surface image saved.
              </div>
            )}

            <div
              style={
                surfaceGrid
              }
            >
              <SummaryItem
                label="Width"
                value={
                  typeof project.aiWidthFt ===
                  "number"
                    ? `${project.aiWidthFt.toFixed(
                        2
                      )} ft`
                    : "—"
                }
              />

              <SummaryItem
                label="Length"
                value={
                  typeof project.aiLengthFt ===
                  "number"
                    ? `${project.aiLengthFt.toFixed(
                        2
                      )} ft`
                    : "—"
                }
              />

              <SummaryItem
                label="Area"
                value={
                  typeof project.areaSqFt ===
                  "number"
                    ? `${project.areaSqFt.toFixed(
                        2
                      )} sq ft`
                    : "—"
                }
              />
            </div>
          </div>
        </>
      )}

      {/* ===================================================
          KITCHEN MODE
          =================================================== */}

      {isKitchen && (
        <>
          {/* ===============================================
              KITCHEN SUMMARY
              =============================================== */}

          <div
            style={
              summaryCard
            }
          >
            <div
              style={
                kitchenSummaryGrid
              }
            >
              <SummaryItem
                label="Layout"
                value={
                  project.layoutLabel ||
                  project.layout ||
                  "—"
                }
              />

              <SummaryItem
                label="Walls measured"
                value={
                  summary.wallsMeasured !=
                  null
                    ? `${summary.wallsMeasured} / ${
                        summary.wallsExpected ??
                        "—"
                      }`
                    : kitchenWalls.length
                }
              />

              <SummaryItem
                label="Total run"
                value={
                  Number.isFinite(
                    Number(
                      summary.totalLinearFt
                    )
                  )
                    ? `${Number(
                        summary.totalLinearFt
                      ).toFixed(
                        1
                      )} ft`
                    : "—"
                }
              />

              <SummaryItem
                label="Wall area"
                value={
                  Number.isFinite(
                    Number(
                      summary.wallAreaSqFt
                    )
                  )
                    ? `${Number(
                        summary.wallAreaSqFt
                      ).toFixed(
                        1
                      )} sq ft`
                    : "—"
                }
              />
            </div>
          </div>

          {/* ===============================================
              2D MEASURED PLAN
              =============================================== */}

          {kitchenWalls.length >
            0 && (
            <div
              style={{
                marginTop: 22,
              }}
            >
              <h3
                style={
                  sectionTitle
                }
              >
                2D Measured Plan
              </h3>

              <div
                style={{
                  marginTop: 10,
                }}
              >
                <KitchenSchematic
                  layout={
                    project.layout
                  }
                  walls={
                    project.walls
                  }
                  projectName={
                    project.projectName
                  }
                  summary={
                    project.summary
                  }
                />
              </div>
            </div>
          )}

          {/* ===============================================
              3D REMOTE SITE SURVEY
              =============================================== */}

          <div
            style={{
              marginTop: 28,
            }}
          >
            <div
              style={
                threeDHeaderRow
              }
            >
              <h3
                style={{
                  ...sectionTitle,
                  margin: 0,
                }}
              >
                3D Remote Site Survey
              </h3>

              {canRenderKitchen3D && (
                <div
                  style={
                    interactiveBadge
                  }
                >
                  Interactive view
                </div>
              )}
            </div>

            <div
              style={
                sectionDivider
              }
            />

            <p
              style={
                threeDDescription
              }
            >
              Generated directly
              from the saved wall
              measurements. Drag to
              rotate, scroll to
              zoom, and right-click
              to pan.
            </p>

            {canRenderKitchen3D ? (
              <Kitchen3DView
                layout={
                  project.layout
                }
                walls={
                  project.walls
                }
              />
            ) : (
              <div
                style={
                  threeDEmpty
                }
              >
                The saved project
                does not contain
                enough measurement
                data to reconstruct
                the 3D survey.
              </div>
            )}
          </div>

          {/* ===============================================
              SOURCE MEASUREMENTS
              =============================================== */}

          <div
            style={{
              marginTop: 30,
            }}
          >
            <h3
              style={
                sectionTitle
              }
            >
              Source Measurements
            </h3>

            <div
              style={
                sourceDescription
              }
            >
              Original wall photos
              and measurement
              overlays used to
              create this kitchen
              survey.
            </div>

            {kitchenWalls.length ===
            0 ? (
              <div
                style={{
                  fontWeight: 800,
                  marginTop: 12,
                }}
              >
                No walls found.
              </div>
            ) : (
              kitchenWalls.map(
                (
                  w,
                  idx
                ) => (
                  <div
                    key={
                      w.role ||
                      idx
                    }
                    style={
                      card
                    }
                  >
                    <div
                      style={
                        roomTitle
                      }
                    >
                      {w.label ||
                        w.role ||
                        `Wall ${
                          idx +
                          1
                        }`}
                    </div>

                    {w.preview ? (
                      <img
                        src={
                          w.preview
                        }
                        alt={
                          w.label ||
                          "wall"
                        }
                        style={
                          imgStyle
                        }
                      />
                    ) : (
                      <div
                        style={
                          emptyNote
                        }
                      >
                        No image
                        saved for
                        this wall.
                      </div>
                    )}

                    <div
                      style={
                        kitchenWallGrid
                      }
                    >
                      <SummaryItem
                        label="Length"
                        value={feetInches(
                          w.lengthFt
                        )}
                      />

                      <SummaryItem
                        label="Height"
                        value={
                          w.heightFt !=
                          null
                            ? feetInches(
                                w.heightFt
                              )
                            : "—"
                        }
                      />
                    </div>
                  </div>
                )
              )
            )}
          </div>
        </>
      )}

      {/* ===================================================
          MULTIROOM MODE
          =================================================== */}

      {!isSurface &&
        !isKitchen && (
          <>
            {/* Summary */}

            <div
              style={
                summaryCard
              }
            >
              <div
                style={
                  summaryGrid
                }
              >
                <SummaryItem
                  label="Rooms"
                  value={
                    summary.roomsCount ??
                    rooms.length ??
                    0
                  }
                />

                <SummaryItem
                  label="Total AI Area"
                  value={
                    Number.isFinite(
                      Number(
                        summary.totalAiArea
                      )
                    )
                      ? `${Number(
                          summary.totalAiArea
                        ).toFixed(
                          2
                        )} sq ft`
                      : "—"
                  }
                />

                <SummaryItem
                  label="Total AI Cost"
                  value={formatMoney(
                    summary.totalAiCost
                  )}
                />

                <SummaryItem
                  label="Total User Area"
                  value={
                    Number.isFinite(
                      Number(
                        summary.totalUserArea
                      )
                    )
                      ? `${Number(
                          summary.totalUserArea
                        ).toFixed(
                          2
                        )} sq ft`
                      : "—"
                  }
                />

                <SummaryItem
                  label="Avg Confidence"
                  value={
                    summary.avgConfidence !=
                    null
                      ? `${summary.avgConfidence}%`
                      : "—"
                  }
                />
              </div>

              <div
                style={
                  summaryFooter
                }
              >
                Total User Cost:{" "}
                {formatMoney(
                  summary.totalUserCost
                )}
              </div>
            </div>

            {/* Rooms */}

            <div
              style={{
                marginTop: 18,
              }}
            >
              <h3
                style={
                  sectionTitle
                }
              >
                Rooms
              </h3>

              {rooms.length ===
              0 ? (
                <div
                  style={{
                    fontWeight: 800,
                  }}
                >
                  No rooms found.
                </div>
              ) : (
                rooms.map(
                  (
                    r,
                    idx
                  ) => (
                    <div
                      key={
                        idx
                      }
                      style={{
                        marginTop: 16,
                      }}
                    >
                      <div
                        style={
                          roomTitle
                        }
                      >
                        {r.label ||
                          `Room ${
                            idx +
                            1
                          }`}
                      </div>

                      <div
                        style={
                          roomGrid
                        }
                      >
                        {r.widthPreview && (
                          <img
                            src={
                              r.widthPreview
                            }
                            alt="width"
                            style={
                              imgStyle
                            }
                          />
                        )}

                        {r.lengthPreview && (
                          <img
                            src={
                              r.lengthPreview
                            }
                            alt="length"
                            style={
                              imgStyle
                            }
                          />
                        )}
                      </div>

                      {Array.isArray(
                        r.comparisonRows
                      ) &&
                        r.comparisonRows
                          .length >
                          0 && (
                          <div
                            style={{
                              marginTop: 10,
                            }}
                          >
                            <ComparisonTable
                              rows={
                                r.comparisonRows
                              }
                            />
                          </div>
                        )}
                    </div>
                  )
                )
              )}
            </div>
          </>
        )}
    </div>
  );
}

/* =========================================================
   SMALL COMPONENTS
   ========================================================= */

function SummaryItem({
  label,
  value,
}) {
  return (
    <div
      style={
        summaryItem
      }
    >
      <div
        style={
          summaryLabel
        }
      >
        {label}
      </div>

      <div
        style={
          summaryValue
        }
      >
        {value ?? "—"}
      </div>
    </div>
  );
}

/* =========================================================
   STYLES
   ========================================================= */

const btnPrimary = {
  padding:
    "10px 14px",

  borderRadius: 10,

  border: "none",

  background:
    "#0b5bd3",

  color: "#fff",

  fontWeight: 900,

  cursor: "pointer",
};

const btnDark = {
  padding:
    "10px 14px",

  borderRadius: 10,

  border: "none",

  background:
    "#111827",

  color: "#fff",

  fontWeight: 900,

  cursor: "pointer",
};

const btnBack = {
  textDecoration:
    "none",

  padding:
    "10px 14px",

  borderRadius: 10,

  background:
    "#6b7280",

  color: "#fff",

  fontWeight: 900,
};

const headerRow = {
  display: "flex",

  justifyContent:
    "space-between",

  alignItems:
    "center",

  gap: 12,
};

const title = {
  margin:
    "8px 0",

  color:
    "#0b3a6a",

  fontWeight: 900,
};

const subtle = {
  fontWeight: 800,

  color:
    "rgba(11,58,106,0.85)",
};

const card = {
  marginTop: 14,

  padding: 14,

  borderRadius: 14,

  background:
    "rgba(243,247,255,0.65)",

  border:
    "1px solid rgba(17,24,39,0.10)",
};

const emptyNote = {
  color: "#777",

  fontWeight: 800,
};

const surfaceGrid = {
  marginTop: 12,

  display: "grid",

  gridTemplateColumns:
    "repeat(3,1fr)",

  gap: 12,
};

const summaryCard = {
  marginTop: 14,

  padding: 12,

  borderRadius: 14,

  background:
    "rgba(243,247,255,0.65)",

  border:
    "1px solid rgba(17,24,39,0.10)",
};

const summaryGrid = {
  display: "grid",

  gridTemplateColumns:
    "repeat(5,1fr)",

  gap: 10,
};

const kitchenSummaryGrid = {
  display: "grid",

  gridTemplateColumns:
    "repeat(4,1fr)",

  gap: 10,
};

const kitchenWallGrid = {
  marginTop: 12,

  display: "grid",

  gridTemplateColumns:
    "repeat(2,1fr)",

  gap: 12,
};

const summaryFooter = {
  marginTop: 10,

  fontWeight: 900,

  color:
    "#0b3a6a",
};

const summaryItem = {
  background:
    "#fff",

  borderRadius: 12,

  padding:
    "10px 12px",
};

const summaryLabel = {
  fontSize: 12,

  fontWeight: 900,

  opacity: 0.6,
};

const summaryValue = {
  fontSize: 16,

  fontWeight: 900,
};

const sectionTitle = {
  color:
    "#0b3a6a",

  fontWeight: 900,
};

const roomTitle = {
  fontWeight: 900,

  color:
    "#0b3a6a",
};

const roomGrid = {
  display: "grid",

  gridTemplateColumns:
    "1fr 1fr",

  gap: 14,
};

const imgStyle = {
  width: "100%",

  marginTop: 10,

  borderRadius: 14,

  border:
    "1px solid rgba(17,24,39,0.12)",

  objectFit:
    "cover",
};

/* =========================================================
   3D SAVED PROJECT STYLES
   ========================================================= */

const threeDHeaderRow = {
  display: "flex",

  alignItems:
    "center",

  justifyContent:
    "space-between",

  gap: 12,
};

const interactiveBadge = {
  color:
    "#0057b8",

  fontWeight: 900,

  fontSize: 14,
};

const sectionDivider = {
  height: 1,

  background:
    "rgba(17,24,39,0.10)",

  marginTop: 10,
};

const threeDDescription = {
  margin:
    "14px 0 12px",

  color:
    "#5c6773",

  fontSize: 15,

  lineHeight: 1.5,
};

const threeDEmpty = {
  padding: 22,

  borderRadius: 14,

  background:
    "rgba(243,247,255,0.65)",

  border:
    "1px solid rgba(17,24,39,0.10)",

  color:
    "#667085",

  fontWeight: 800,
};

const sourceDescription = {
  marginTop: -4,

  marginBottom: 6,

  color:
    "#667085",

  fontSize: 14,

  lineHeight: 1.5,
};
